import { Search } from "lucide-react";
import { useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function HelpPage() {
	const [searchQuery, setSearchQuery] = useState("");

	const filteredQuestions = [
		"How do I find a contract",
		"How to report a vendor",
		"How to reject an order",
		"Managing my Intasida profile"
	].filter(question => question.toLowerCase().includes(searchQuery.toLowerCase()));

	return (
		<div className="px-4 py-2">
			<div className="container mx-auto max-w-3xl mt-5">
				{/* Header Section */}
				<div className="text-center space-y-4 mb-12">
					<h1 className="text-3xl md:text-4xl font-semibold">Hello, how can we help you?</h1>
					<p className="text-gray-600 max-w-2xl mx-auto">
						Use the search button to explore and search various topics or find answers to your questions on how to use
						the Intasida App, or how our operators can assist you
					</p>
				</div>

				{/* Search Section */}
				<div className="relative mb-12">
					<Input
						type="text"
						placeholder="Search your keyword"
						className="w-full pl-4 pr-12 h-12 rounded-lg bg-white"
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
					/>
					<Button size="icon" className="absolute right-1 top-1 h-10 w-10">
						<Search className="h-4 w-4" />
					</Button>
				</div>

				{/* FAQ Section */}
				<div className="bg-white rounded-lg p-6 shadow-sm">
					<h2 className="text-xl font-semibold mb-6">Frequently Asked Questions</h2>
					<Accordion type="single" collapsible className="w-full">
						{filteredQuestions.includes("How do I find a contract") && (
							<AccordionItem value="item-0">
								<AccordionTrigger className="text-left">How do I find a contract</AccordionTrigger>
								<AccordionContent>
									To find an existing contract in Intasida, follow these steps:
									<li>Log in to your Intasida account</li>
									<li>Navigate to the 'Contracts' section in the dashboard</li>
									<li>Use the search bar to filter contracts by vendor name, date, or contract ID</li>
									<li>Click on any contract to view its full details</li>
									<li>You can also use the advanced filters to narrow down your search by status, date range, or contract type</li>
								</AccordionContent>
							</AccordionItem>
						)}

						{filteredQuestions.includes("How to report a vendor") && (
							<AccordionItem value="item-1">
								<AccordionTrigger className="text-left">How to report a vendor</AccordionTrigger>
								<AccordionContent>
									If you need to report a vendor, here's what to do:
									<li>Go to the vendor's profile page</li>
									<li>Click the 'Report Vendor' button</li>
									<li>Select the reason for your report from the dropdown menu</li>
									<li>Provide detailed information about the issue</li>
									<li>Attach any relevant evidence (screenshots, documents)</li>
									<li>Submit the report</li>

									Our team will review your report within 24-48 hours and take appropriate action.
								</AccordionContent>
							</AccordionItem>
						)}

						{filteredQuestions.includes("How to reject an order") && (
							<AccordionItem value="item-2">
								<AccordionTrigger className="text-left">How to reject an order</AccordionTrigger>
								<AccordionContent>
									To reject an order in Intasida:
									<li>Open the order details page</li>
									<li>Click the 'Reject Order' button</li>
									<li>Select a reason for rejection from the provided options</li>
									<li>Add any additional comments explaining your decision</li>
									<li>Confirm the rejection</li>

									Note: You can only reject orders that are in 'Pending' status. Once rejected, the vendor will be notified and can either modify the order or cancel it.
								</AccordionContent>
							</AccordionItem>
						)}

						{filteredQuestions.includes("Managing my Intasida profile") && (
							<AccordionItem value="item-3">
								<AccordionTrigger className="text-left">Managing my Intasida profile</AccordionTrigger>
								<AccordionContent>
									To manage your Intasida profile effectively:
									<li>Click on your profile picture in the top right corner</li>
									<li>Select 'Profile Settings'</li>
									<li>Update your personal information, company details, and contact information</li>
									<li>Set your notification preferences</li>
									<li>Manage your security settings and password</li>
									<li>Configure your payment methods and billing information</li>

									Remember to save any changes you make to your profile.
								</AccordionContent>
							</AccordionItem>
						)}
					</Accordion>
				</div>
			</div>
		</div>
	);
}
